import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { HomepageHighlight } from '../types/cms';

interface Props {
  slides: HomepageHighlight[];
  height?: { xs: number; sm: number; md: number; lg: number };
}

const MiniSlider: React.FC<Props> = ({
  slides,
  height = { xs: 280, sm: 380, md: 480, lg: 520 },
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [idx, setIdx] = useState(0);

  /* autoplay */
  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => setIdx((p) => (p + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [slides.length]);

  if (!slides.length) return null;
  const current = slides[idx];

  /* helpers para CTA */
  const related = (() => {
    if (current.relatedPackage)
      return {
        url: `/paquetes/${current.relatedPackage.slug}`,
        img: current.relatedPackage.imageCover?.publicUrl,
      };
    if (current.relatedPost)
      return {
        url: `/blog/${current.relatedPost.slug}`,
        img: current.relatedPost.coverImage?.publicUrl,
      };
    if (current.relatedDestination)
      return {
        url: `/destinos/${current.relatedDestination.slug}`,
        img: current.relatedDestination.imageCover?.publicUrl,
      };
    return {};
  })();

  const bg = current.image?.publicUrl || (related as any).img || '';

  return (
    <Box sx={{ mb: { xs: 4, md: 6 }, position: 'relative' }}>
      {/* contenedor fijo */}
      <Box
        sx={{
          position: 'relative',
          height,
          width: '100%',
          overflow: 'hidden',
          borderRadius: { xs: 1, md: 2 },
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}
      >
        {/* slides como background-image */}
        {slides.map((h, i) => (
          <Box
            key={h.id}
            sx={{
              position: 'absolute',
              inset: 0,
              transition: 'opacity 0.9s ease-in-out',
              opacity: i === idx ? 1 : 0,
              backgroundImage: `url(${
                h.image?.publicUrl ||
                h.relatedPackage?.imageCover?.publicUrl ||
                h.relatedPost?.coverImage?.publicUrl ||
                h.relatedDestination?.imageCover?.publicUrl ||
                ''
              })`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        ))}

        {/* gradiente suave encima de la foto */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(45deg, rgba(0,0,0,0.25), rgba(0,0,0,0.05))',
          }}
        />

        {/* overlay con título / descripción / CTA */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            zIndex: 2,
            width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
            maxWidth: 600,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(0, 95, 139, 0.85)',
              backdropFilter: 'blur(8px)',
              borderRadius: { xs: 1.5, md: 2 },
              p: { xs: 2.5, sm: 3.5, md: 4 },
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: { xs: 1, md: 1.5 },
                fontSize: {
                  xs: '1.75rem',
                  sm: '2.25rem',
                  md: '2.75rem',
                  lg: '3rem',
                },
                lineHeight: 1.2,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              {current.title}
            </Typography>

            {current.description && (
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                  fontWeight: 500,
                  mb: { xs: 2, md: 2.5 },
                  opacity: 0.95,
                  lineHeight: 1.4,
                }}
              >
                {current.description}
              </Typography>
            )}

            {current.ctaText && (
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                href={current.ctaUrl || (related as any).url || '#'}
                sx={{
                  backgroundColor: '#ff66c4',
                  fontWeight: 600,
                  px: { xs: 3, md: 4 },
                  py: { xs: 1, md: 1.5 },
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 16px rgba(255, 102, 196, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#e55bb0',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(255, 102, 196, 0.5)',
                  },
                }}
              >
                {current.ctaText}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* navegación */}
      {slides.length > 1 && (
        <>
          <IconButton
            onClick={() => setIdx((p) => (p - 1 + slides.length) % slides.length)}
            sx={navBtn({ left: { xs: 8, md: 16 }, isMobile })}
          >
            <ChevronLeft fontSize={isMobile ? 'medium' : 'large'} />
          </IconButton>

          <IconButton
            onClick={() => setIdx((p) => (p + 1) % slides.length)}
            sx={navBtn({ right: { xs: 8, md: 16 }, isMobile })}
          >
            <ChevronRight fontSize={isMobile ? 'medium' : 'large'} />
          </IconButton>

          {/* dots */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 12, md: 20 },
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: { xs: 0.75, md: 1 },
              zIndex: 3,
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 2,
              p: { xs: 0.75, md: 1 },
              backdropFilter: 'blur(8px)',
            }}
          >
            {slides.map((_, i) => (
              <Box
                key={i}
                onClick={() => setIdx(i)}
                sx={{
                  width: { xs: 10, md: 12 },
                  height: { xs: 10, md: 12 },
                  borderRadius: '50%',
                  backgroundColor:
                    i === idx ? 'white' : 'rgba(255,255,255,0.6)',
                  cursor: 'pointer',
                  transition: 'transform .15s',
                  transform: i === idx ? 'scale(1.3)' : 'scale(1)',
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

/* estilo común para flechas */
const navBtn = ({ left, right, isMobile }: any) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  ...(left ? { left } : { right }),
  backgroundColor: 'rgba(255,255,255,0.9)',
  backdropFilter: 'blur(8px)',
  color: 'rgba(0,0,0,0.7)',
  zIndex: 3,
  width: { xs: 40, md: 48 },
  height: { xs: 40, md: 48 },
  border: '1px solid rgba(255,255,255,0.2)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,1)',
    transform: 'translateY(-50%) scale(1.05)',
  },
});

export default MiniSlider;
